import { CONNECTION_STATE_CHANGE, ConnectionState } from 'aws-amplify/api';
import { Hub } from 'aws-amplify/utils';
import { createContext, useContext, useEffect, useState } from 'react';
import { usePrevious } from '../react.helper';
import { captureSentry } from '../sentryHelper';

// notes on states :
// Connected - Connected and working with no issues.
// ConnectedPendingDisconnect - The connection has no active subscriptions and is disconnecting.
// ConnectedPendingKeepAlive - The connection is open, but has missed expected keep-alive messages.
// ConnectedPendingNetwork - The connection is open, but the network connection has been disrupted. When the network recovers, the connection will continue serving traffic.
// Connecting - Attempting to connect.
// ConnectionDisrupted - The connection is disrupted and the network is available.
// ConnectionDisruptedPendingNetwork - The connection is disrupted and the network connection is unavailable.
// Disconnected - Connection has no active subscriptions and is disconnecting.

type PubSubContextType = {
  state: ConnectionState | null;
};

const PubSubContext = createContext<PubSubContextType>({
  state: null,
});

export const usePubSubContext = () => {
  const context = useContext(PubSubContext);
  if (!context) {
    throw new Error('usePubSubStateContext must be used within a PubSubProvider');
  }
  return context;
};

export const PubSubProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<ConnectionState | null>(null);
  const previousState = usePrevious(state);

  useEffect(() => {
    const result = Hub.listen('api', (data: any) => {
      const { payload } = data;

      if (payload.event === CONNECTION_STATE_CHANGE) {
        const connectionState = payload.data.connectionState as ConnectionState;
        console.log('DEBUG >> Connection state change: ', connectionState);
        setState(connectionState);

        if (
          previousState === ConnectionState.Connected &&
          (connectionState === ConnectionState.Disconnected ||
            connectionState === ConnectionState.ConnectionDisrupted)
        ) {
          captureSentry({
            title: 'Aws Connection status change',
            detail: {
              newState: JSON.stringify(connectionState),
            },
          });
        }
      }
    });

    return () => {
      // result of Hub.listen is a method to stop listening
      result();
    };
  }, [previousState]);

  return <PubSubContext.Provider value={{ state }}>{children}</PubSubContext.Provider>;
};
